import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Registry, type ScienceTutorContext, type Section } from './scienceTutorContext';
import { MathToolTutor } from './MathToolTutor';
import { STUDY_TOOLS } from './studyTools';
import { resolveStudyToolYear } from './studyToolYear';

export function ScienceTutorWorkspace({ context, children }: { context: ScienceTutorContext; children: ReactNode }) {
  const [sections, setSections] = useState<Record<string, Section>>({});
  const update = useCallback((id: string, section: Section | null) => {
    setSections((old) => {
      if (section) return { ...old, [id]: section };
      if (!old[id]) return old;
      const next = { ...old };
      delete next[id];
      return next;
    });
  }, []);
  const registry = useMemo(() => ({ update }), [update]);
  const [params] = useSearchParams();
  const location = useLocation();
  const tool = STUDY_TOOLS.find((item) => item.subjectKey === context.subjectKey && item.id === context.toolId);
  const year = resolveStudyToolYear(params.get('year'), null) ?? tool?.availableFrom ?? 7;
  const isStudent = location.pathname.startsWith(`/learn/study/subjects/${context.subjectKey}/tools/`);
  return (
    <Registry.Provider value={registry}>
      {isStudent && tool && (
        <div className="science-tutor-launcher mb-3 flex items-center justify-end gap-3">
          <span className="text-xs text-slate2">Ask about this experiment</span>
          <MathToolTutor subjectKey={context.subjectKey} toolId={context.toolId} name={tool.name} year={year}
            state={{ ...context.state, active_sections: Object.values(sections), context_source: 'Learner inputs and explicitly shared observations; not grading evidence' }} />
        </div>
      )}
      <div className="science-tool-content">{children}</div>
    </Registry.Provider>
  );
}
